import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useApp } from '../context/AppContext';
import { useCurator } from '../context/CuratorContext';
import * as API from '../lib/api';
import AnalysisView from '../components/analysis/AnalysisView';
import ConsensusOverlay from '../components/analysis/ConsensusOverlay';

const clampSpanPct = (value) => Math.min(100, Math.max(0, Number(value) || 0));

const normalizeAnalysisSpan = (span) => {
  if (!span || typeof span !== 'object') return { start_pct: 0, end_pct: 100 };
  const start = clampSpanPct(span.start_pct);
  const end = clampSpanPct(span.end_pct);
  const low = Math.min(start, end);
  const high = Math.max(start, end);
  if (high - low < 1) {
    return { start_pct: low, end_pct: Math.min(100, low + 1) };
  }
  return { start_pct: low, end_pct: high };
};

const DEFAULT_REPORT_SECTIONS = [
  'Executive Summary', 'Visual Evidence', 'Wave Analysis', 'Wall Street Narrative',
  'Indicators', 'Confidence Ledger', 'Fibonacci Matrix', 'Time Projections', 'Divergences',
  'Wave Degree', 'Risk/Reward', 'Confluence', 'Sentiment', 'Fractal Analysis',
  'Heuristics', 'Methodology',
];

const buildAnalysisChatStorageKey = (projectId, analysisId) => {
  const projectKey = projectId || 'no-project';
  const analysisKey = analysisId || 'active';
  return `wavekit.analysis.chat.${projectKey}.${analysisKey}`;
};

const buildAnalysisUiStorageKey = (projectId, analysisId) => {
  const projectKey = projectId || 'no-project';
  const analysisKey = analysisId || 'active';
  return `wavekit.analysis.ui.${projectKey}.${analysisKey}`;
};

const buildProjectActiveAnalysisKey = (projectId) => {
  const projectKey = projectId || 'no-project';
  return `wavekit.analysis.active.${projectKey}`;
};

const readBrowserJson = (key) => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};

const writeBrowserJson = (key, value) => {
  if (typeof window === 'undefined') return;
  try {
    if (value == null) {
      window.localStorage.removeItem(key);
      return;
    }
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (_) {
    // Ignore storage quota failures.
  }
};

const upsertProjectAnalysis = (project, analysisDoc) => {
  if (!project || !analysisDoc || project.id !== analysisDoc.project_id) return project;
  const analyses = Array.isArray(project.analyses) ? project.analyses : [];
  const nextAnalyses = [analysisDoc, ...analyses.filter((entry) => entry?.id !== analysisDoc.id)];
  return {
    ...project,
    analyses: nextAnalyses,
    analysis_count: Math.max(Number(project.analysis_count || 0), nextAnalyses.length),
  };
};

/** Resolve chart view: non-image analyses must not land on Original (no source image). */
export const resolveChartView = (storedView, sourceType) => {
  const view = storedView || 'annotated';
  if (sourceType && sourceType !== 'image_upload' && view === 'original') return 'clean';
  return view;
};

export default function Analysis() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentProject, setCurrentProject, currentAnalysis, setCurrentAnalysis } = useApp();
  const { isCurator, setLanding } = useCurator();
  const requestedAnalysisId = searchParams.get('analysisId');
  const requestedChartView = searchParams.get('chartView');
  const [landingBusy, setLandingBusy] = useState(false);

  // Local UI State
  const [analyzing, setAnalyzing] = useState(false);
  const [jobProgress, setJobProgress] = useState(0);
  const [chartView, setChartView] = useState(() =>
    resolveChartView(
      requestedChartView
        || (typeof window !== 'undefined' ? window.localStorage.getItem('wavekit.analysis.chartView') : null),
      undefined,
    ),
  ); // 'annotated', 'clean', 'original'
  const [showFibs, setShowFibs] = useState(() => (typeof window !== 'undefined' ? window.localStorage.getItem('wavekit.analysis.showFibs') : 'true') !== 'false');
  const [showTimeframe, setShowTimeframe] = useState(() => (typeof window !== 'undefined' ? window.localStorage.getItem('wavekit.analysis.showTimeframe') : 'false') === 'true');
  const [predictionSteps] = useState(7);

  // Data State (Derived or additional fetch)
  const [predictions, setPredictions] = useState(null);
  const [predictionsLoading, setPredictionsLoading] = useState(false);
  const [timeframeMethods, setTimeframeMethods] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState(0);

  // UI Expansion
  const [expandedPredictions, setExpandedPredictions] = useState(true);
  const [expandedExplain, setExpandedExplain] = useState(false);
  const [expandedFibs, setExpandedFibs] = useState(false);

  // Candidate Selection
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [selectedCandidateIdx, setSelectedCandidateIdx] = useState(0);
  const [chartMetadata, setChartMetadata] = useState(null);
  const [rescoreBusy, setRescoreBusy] = useState(false);
  const [strictnessInterval, setStrictnessInterval] = useState(null);
  const [strictnessProbe, setStrictnessProbe] = useState(0.65);
  const [strictnessPreview, setStrictnessPreview] = useState(null);
  const [strictnessPreviewLoading, setStrictnessPreviewLoading] = useState(false);
  const [strictnessApplyBusy, setStrictnessApplyBusy] = useState(false);
  const [strictnessPreviewNonce, setStrictnessPreviewNonce] = useState(0);
  const [fractalEnabled, setFractalEnabled] = useState(false);
  const [fractalTree, setFractalTree] = useState(null);
  const [fractalView, setFractalView] = useState(null);
  const [fractalPath, setFractalPath] = useState([]);
  const [fractalLoading, setFractalLoading] = useState(false);
  const [reportBusy, setReportBusy] = useState(false);
  const [doodleSubmitting, setDoodleSubmitting] = useState(false);
  const [lastRescoreResult, setLastRescoreResult] = useState(null);
  const [completionPredictionSteps, setCompletionPredictionSteps] = useState(0);
  const [analysisFocus, setAnalysisFocus] = useState(() => (typeof window !== 'undefined' ? window.localStorage.getItem('wavekit.analysis.focus') : null) || 'macro');
  const [lockScopeMode, setLockScopeMode] = useState(() => (typeof window !== 'undefined' ? window.localStorage.getItem('wavekit.analysis.lockScopeMode') : 'false') === 'true');
  const [analysisSpan, setAnalysisSpan] = useState(() => {
    if (typeof window === 'undefined') return { start_pct: 0, end_pct: 100 };
    const raw = window.localStorage.getItem('wavekit.analysis.span');
    if (!raw) return { start_pct: 0, end_pct: 100 };
    try {
      return normalizeAnalysisSpan(JSON.parse(raw));
    } catch (_) {
      return { start_pct: 0, end_pct: 100 };
    }
  });

  // Chat
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);

  // Refs
  const fileRef = useRef(null);
  const chatEndRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [aiGate, setAiGate] = useState({
    checked: false,
    required: true,
    provider: 'google',
    ready: true,
    hasProviderKey: false,
    hasFallbackKey: false,
    hasOpenRouterKey: false,
    hasGoogleKey: false,
  });
  const [selectedReportSections, setSelectedReportSections] = useState(() => DEFAULT_REPORT_SECTIONS);

  // Consensus Engine State
  const [consensusRunning, setConsensusRunning] = useState(false);
  const [consensusAnalysisId, setConsensusAnalysisId] = useState(null);

  const selectedCandidateId = selectedCandidate?.id || null;
  const chatStorageKey = buildAnalysisChatStorageKey(projectId, currentAnalysis?.id);
  const analysisUiStorageKey = buildAnalysisUiStorageKey(projectId, currentAnalysis?.id);
  const activeAnalysisStorageKey = buildProjectActiveAnalysisKey(projectId);
  const [persistedAnalysisUi, setPersistedAnalysisUi] = useState(null);
  const manualPresentationMode = ((currentAnalysis?.metadata?.presentation_mode || '') === 'manual_only') || !!currentAnalysis?.metadata?.manual_anchor_mode;
  const strictnessRestoreAvailable = Boolean(currentAnalysis?.metadata?.strictness_restore_snapshot?.candidates?.length);

  const computeCompletionPredictionSteps = (candidate) => {
    if (!candidate) return 0;

    const labels = new Set((candidate.labels || candidate.pivots || []).map((entry) => {
      if (typeof entry === 'string') return entry.toUpperCase();
      return String(entry?.label || '').toUpperCase();
    }));

    const has = (label) => labels.has(label);
    const pattern = String(candidate.pattern_type || '').toLowerCase();

    if (pattern === 'impulse') {
      const motiveOrder = ['1', '2', '3', '4', '5'];
      const firstMissingIdx = motiveOrder.findIndex((label) => !has(label));
      if (firstMissingIdx >= 0) {
        return motiveOrder.length - firstMissingIdx;
      }

      if (!has('A')) return 3;
      if (!has('B')) return 2;
      if (!has('C')) return 1;
      return 0;
    }

    if (!has('A')) return 3;
    if (!has('B')) return 2;
    if (!has('C')) return 1;
    return 0;
  };

  // Cleanup preview URLs
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const skipChartViewPersistRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (skipChartViewPersistRef.current) {
      skipChartViewPersistRef.current = false;
      return;
    }
    window.localStorage.setItem('wavekit.analysis.chartView', chartView);
  }, [chartView]);

  // B3: snap away from Original when analysis has no source image (do not write to localStorage).
  useEffect(() => {
    if (
      currentAnalysis?.source_type
      && currentAnalysis.source_type !== 'image_upload'
      && chartView === 'original'
    ) {
      skipChartViewPersistRef.current = true;
      setChartView('clean');
    }
  }, [currentAnalysis?.id, currentAnalysis?.source_type, chartView]);

  // Landing pin may pass chartView query — honor once when analysis loads
  useEffect(() => {
    if (!requestedChartView) return;
    const next = resolveChartView(requestedChartView, currentAnalysis?.source_type);
    if (next && next !== chartView) {
      skipChartViewPersistRef.current = true;
      setChartView(next);
    }
    // Only re-apply when the query or analysis id changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedChartView, currentAnalysis?.id]);

  const handleSetAsLanding = async () => {
    if (!isCurator || landingBusy) return;
    const pid = currentProject?.id || projectId;
    const aid = currentAnalysis?.id;
    if (!pid || !aid) {
      toast.error('No analysis to pin', { description: 'Open an analysis first.' });
      return;
    }
    setLandingBusy(true);
    try {
      await setLanding({ projectId: pid, analysisId: aid, chartView });
      toast.success('Landing updated', {
        description: 'Visitors will land on this analysis and chart view.',
      });
    } catch (err) {
      const status = Number(err?.status || err?.response?.status || 0);
      toast.error(status === 401 ? 'Curator session expired' : 'Could not pin landing', {
        description: status === 401 ? 'Log in again as curator.' : (err?.message || 'Try again.'),
      });
    } finally {
      setLandingBusy(false);
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('wavekit.analysis.showFibs', String(showFibs));
  }, [showFibs]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('wavekit.analysis.showTimeframe', String(showTimeframe));
  }, [showTimeframe]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('wavekit.analysis.focus', analysisFocus);
  }, [analysisFocus]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('wavekit.analysis.lockScopeMode', String(lockScopeMode));
  }, [lockScopeMode]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('wavekit.analysis.span', JSON.stringify(normalizeAnalysisSpan(analysisSpan)));
  }, [analysisSpan]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const raw = window.localStorage.getItem(chatStorageKey);
    if (!raw) {
      setChatMessages([]);
      setChatInput('');
      return;
    }

    try {
      const parsed = JSON.parse(raw);
      setChatMessages(Array.isArray(parsed?.messages) ? parsed.messages : []);
      setChatInput(typeof parsed?.input === 'string' ? parsed.input : '');
    } catch (_) {
      setChatMessages([]);
      setChatInput('');
    }
  }, [chatStorageKey]);

  useEffect(() => {
    const persisted = readBrowserJson(analysisUiStorageKey) || {};
    setPersistedAnalysisUi(persisted);

    if (typeof persisted.expandedPredictions === 'boolean') setExpandedPredictions(persisted.expandedPredictions);
    if (typeof persisted.expandedExplain === 'boolean') setExpandedExplain(persisted.expandedExplain);
    if (typeof persisted.expandedFibs === 'boolean') setExpandedFibs(persisted.expandedFibs);
    if (typeof persisted.fractalEnabled === 'boolean') setFractalEnabled(persisted.fractalEnabled);
    if (typeof persisted.strictnessProbe === 'number') setStrictnessProbe(persisted.strictnessProbe);
    if (typeof persisted.selectedMethod === 'number') setSelectedMethod(persisted.selectedMethod);
    if (Array.isArray(persisted.selectedReportSections) && persisted.selectedReportSections.length > 0) {
      setSelectedReportSections(persisted.selectedReportSections);
    } else {
      setSelectedReportSections(DEFAULT_REPORT_SECTIONS);
    }
  }, [analysisUiStorageKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(chatStorageKey, JSON.stringify({
      messages: Array.isArray(chatMessages) ? chatMessages.slice(-80) : [],
      input: chatInput,
    }));
  }, [chatStorageKey, chatMessages, chatInput]);

  useEffect(() => {
    writeBrowserJson(analysisUiStorageKey, {
      selectedCandidateId: selectedCandidate?.id || null,
      selectedCandidateIdx,
      expandedPredictions,
      expandedExplain,
      expandedFibs,
      fractalEnabled,
      strictnessProbe,
      selectedMethod,
      selectedReportSections,
    });
  }, [analysisUiStorageKey, selectedCandidate?.id, selectedCandidateIdx, expandedPredictions, expandedExplain, expandedFibs, fractalEnabled, strictnessProbe, selectedMethod, selectedReportSections]);

  useEffect(() => {
    if (typeof window === 'undefined' || !projectId) return;
    const nextId = currentAnalysis?.id;
    if (!nextId) return;
    window.localStorage.setItem(activeAnalysisStorageKey, nextId);
  }, [activeAnalysisStorageKey, projectId, currentAnalysis?.id]);

  useEffect(() => {
    let cancelled = false;
    const loadAIGate = async () => {
      try {
        const [settingsData, rawKeys] = await Promise.all([API.getSettings(), API.getRawKeys()]);
        if (cancelled) return;

        const provider = settingsData?.model_config_setting?.vision_provider || 'google';
        const required = settingsData?.rule_settings?.require_ai_assist ?? true;
        const hasOpenRouterKey = !!rawKeys?.openrouter_configured;
        const hasGoogleKey = !!rawKeys?.google_ai_configured;
        const hasProviderKey = provider === 'google' ? hasGoogleKey : hasOpenRouterKey;
        const hasFallbackKey = provider === 'google' ? hasOpenRouterKey : hasGoogleKey;

        setAiGate({
          checked: true,
          required,
          provider,
          ready: !required || hasProviderKey || hasFallbackKey,
          hasProviderKey,
          hasFallbackKey,
          hasOpenRouterKey,
          hasGoogleKey,
        });
      } catch (error) {
        if (!cancelled) {
          setAiGate(prev => ({ ...prev, checked: true }));
        }
      }
    };

    loadAIGate();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!projectId) return;

    let cancelled = false;
    const loadProjectData = async () => {
      try {
        const project = await API.getProject(projectId);
        if (cancelled) return;

        setCurrentProject(project);

        const analyses = project?.analyses || [];
        const persistedActiveAnalysisId = typeof window !== 'undefined'
          ? window.localStorage.getItem(activeAnalysisStorageKey)
          : null;
        const requestedAnalysis = requestedAnalysisId
          ? analyses.find((entry) => entry.id === requestedAnalysisId) || null
          : null;
        const matchingAnalysis = currentAnalysis?.project_id === projectId
          ? analyses.find(a => a.id === currentAnalysis.id) || currentAnalysis
          : null;
        const persistedAnalysis = analyses.find((entry) => entry.id === persistedActiveAnalysisId) || null;

        setCurrentAnalysis(requestedAnalysis || matchingAnalysis || persistedAnalysis || analyses[0] || null);
      } catch (e) {
        console.error('Failed to load project context:', e);
        API.showFriendlyApiError(e, 'load the project context', { id: 'analysis-project-context' });
      }
    };

    if (!currentProject || currentProject.id !== projectId || !currentAnalysis || currentAnalysis.project_id !== projectId) {
      loadProjectData();
    }

    return () => {
      cancelled = true;
    };
  }, [projectId, currentProject, currentAnalysis, setCurrentProject, setCurrentAnalysis, activeAnalysisStorageKey, requestedAnalysisId]);

  // ── Sync Selection ──
  useEffect(() => {
    const candidates = currentAnalysis?.candidates || [];
    if (!candidates.length) return;

    const persistedCandidateId = persistedAnalysisUi?.selectedCandidateId;
    const persistedCandidateIdx = Number.isInteger(persistedAnalysisUi?.selectedCandidateIdx)
      ? persistedAnalysisUi.selectedCandidateIdx
      : -1;
    const persistedCandidate = candidates.find((candidate) => candidate.id === persistedCandidateId)
      || (persistedCandidateIdx >= 0 ? candidates[persistedCandidateIdx] : null);

    if (!selectedCandidate) {
      const preferred = persistedCandidate || candidates.find(c => c.preferred) || candidates[0];
      setSelectedCandidate(preferred);
      setSelectedCandidateIdx(candidates.indexOf(preferred));
      return;
    }

    const updated = candidates.find((candidate) => candidate.id === selectedCandidate.id);
    if (updated) {
      setSelectedCandidate(updated);
      setSelectedCandidateIdx(candidates.indexOf(updated));
      return;
    }

    const fallback = persistedCandidate || candidates.find(c => c.preferred) || candidates[0];
    setSelectedCandidate(fallback);
    setSelectedCandidateIdx(candidates.indexOf(fallback));
  }, [currentAnalysis, selectedCandidate, persistedAnalysisUi]);

  useEffect(() => {
    setCompletionPredictionSteps(computeCompletionPredictionSteps(selectedCandidate));
  }, [selectedCandidate]);

  // ── Chart Metadata for Drag-to-Rescore ──
  useEffect(() => {
    if (!currentAnalysis?.id || !selectedCandidateId) {
      setChartMetadata(null);
      return;
    }

    let cancelled = false;
    const fetchChartMetadata = async () => {
      try {
        const metadata = await API.getChartMetadata(currentAnalysis.id, {
          candidateId: selectedCandidateId,
          candidateIndex: selectedCandidateIdx,
        });
        if (!cancelled) setChartMetadata(metadata);
      } catch (error) {
        if (!cancelled) setChartMetadata(null);
        console.error('Chart metadata fetch failed:', error);
        API.showFriendlyApiError(error, 'load chart metadata', { id: 'analysis-chart-metadata' });
      }
    };

    fetchChartMetadata();
    return () => {
      cancelled = true;
    };
  }, [currentAnalysis?.id, currentAnalysis?.last_rescored_at, selectedCandidateId, selectedCandidateIdx]);

  // ── Strictness Confidence Interval ──
  useEffect(() => {
    if (!currentAnalysis?.id || !selectedCandidateId) {
      setStrictnessInterval(null);
      return;
    }

    let cancelled = false;
    const fetchStrictnessInterval = async () => {
      try {
        const data = await API.getStrictnessInterval(currentAnalysis.id, {
          candidateId: selectedCandidateId,
          candidateIndex: selectedCandidateIdx,
        });
        if (cancelled) return;
        setStrictnessInterval(data);

        const currentStrictness = data?.current_setting?.strictness;
        const bestStrictness = data?.best?.strictness;
        if (typeof currentStrictness === 'number') {
          setStrictnessProbe(currentStrictness);
        } else if (typeof bestStrictness === 'number') {
          setStrictnessProbe(bestStrictness);
        }
      } catch (error) {
        if (!cancelled) setStrictnessInterval(null);
        console.error('Strictness interval fetch failed:', error);
        API.showFriendlyApiError(error, 'load the strictness interval', { id: 'analysis-strictness' });
      }
    };

    fetchStrictnessInterval();
    return () => {
      cancelled = true;
    };
    }, [currentAnalysis?.id, currentAnalysis?.last_rescored_at, selectedCandidateId, selectedCandidateIdx]);

  useEffect(() => {
    if (!currentAnalysis?.id || !selectedCandidateId) {
      setStrictnessPreview(null);
      setStrictnessPreviewLoading(false);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setStrictnessPreviewLoading(true);
      try {
        const data = await API.getStrictnessPreview(currentAnalysis.id, {
          candidateId: selectedCandidateId,
          candidateIndex: selectedCandidateIdx,
          strictness: strictnessProbe,
          maxTotal: 3,
        });
        if (!cancelled) {
          setStrictnessPreview(data);
        }
      } catch (error) {
        if (!cancelled) {
          setStrictnessPreview(null);
        }
        console.error('Strictness preview failed:', error);
        API.showFriendlyApiError(error, 'preview that strictness spotlight', { id: 'analysis-strictness-preview' });
      } finally {
        if (!cancelled) {
          setStrictnessPreviewLoading(false);
        }
      }
    }, 180);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [currentAnalysis?.id, currentAnalysis?.last_rescored_at, selectedCandidateId, selectedCandidateIdx, strictnessProbe, strictnessPreviewNonce]);

  const buildFractalRootView = (tree) => {
    if (!tree) return null;
    const rootWaves = tree.root_waves || [];
    const labels = rootWaves.map(w => w.wave_label).join(' - ') || 'Root';

    return {
      zoom_level: 0,
      degree: tree.root_degree || 'UNKNOWN',
      parent_label: 'Root',
      breadcrumb: [labels],
      waves: rootWaves,
      verification_score: null,
      narrative: tree.verification_summary || 'Fractal overview ready.',
    };
  };

  // ── Fractal View ──
  useEffect(() => {
    if (!fractalEnabled || !currentAnalysis?.id || !selectedCandidateId) {
      setFractalTree(null);
      setFractalView(null);
      setFractalPath([]);
      return;
    }

    let cancelled = false;
    const fetchFractalTree = async () => {
      setFractalLoading(true);
      try {
        const tree = await API.getFractalTree(currentAnalysis.id, {
          candidateId: selectedCandidateId,
          candidateIndex: selectedCandidateIdx,
        });
        if (cancelled) return;
        setFractalTree(tree);
        setFractalView(buildFractalRootView(tree));
        setFractalPath([]);
      } catch (error) {
        if (!cancelled) {
          setFractalTree(null);
          setFractalView(null);
        }
        console.error('Fractal tree fetch failed:', error);
        API.showFriendlyApiError(error, 'load the fractal tree', { id: 'analysis-fractal-tree' });
      } finally {
        if (!cancelled) setFractalLoading(false);
      }
    };

    fetchFractalTree();
    return () => {
      cancelled = true;
    };
  }, [fractalEnabled, currentAnalysis?.id, selectedCandidateId, selectedCandidateIdx]);

  // ── Predictions ──
  useEffect(() => {
    if (selectedCandidateId && currentAnalysis?.id && predictionSteps > 0) {
      const fetchPredictions = async () => {
        setPredictionsLoading(true);
        try {
          const data = await API.getWavePredictions(currentAnalysis.id, {
            candidateId: selectedCandidateId,
            candidateIndex: selectedCandidateIdx,
            predictionSteps,
          });
          setPredictions(data);
        } catch (e) {
          console.error('Prediction failed:', e);
          API.showFriendlyApiError(e, 'project the next wave path', { id: 'analysis-predictions' });
        } finally {
          setPredictionsLoading(false);
        }
      };
      const timer = setTimeout(fetchPredictions, 500);
      return () => clearTimeout(timer);
    }
    setPredictions(null);
  }, [selectedCandidateId, selectedCandidateIdx, predictionSteps, currentAnalysis?.id]);

  // ── Timeframe Methods ──
  useEffect(() => {
    if (showTimeframe && selectedCandidateId && currentAnalysis?.id) {
      const fetchTimeframes = async () => {
        try {
          const data = await API.getTimeframePrediction(currentAnalysis.id, {
            candidateId: selectedCandidateId,
            candidateIndex: selectedCandidateIdx,
            method: 'all',
          });
          setTimeframeMethods(data.methods || []);
        } catch (e) {
          console.error('Timeframe fetch failed:', e);
          API.showFriendlyApiError(e, 'estimate the timeframe window', { id: 'analysis-timeframes' });
        }
      };
      fetchTimeframes();
    }
  }, [showTimeframe, selectedCandidateId, selectedCandidateIdx, currentAnalysis?.id]);

  // ── Callbacks ──
  const analyzeImageFile = async (file, { fromDoodle = false, userPivots = [], overrideSpan = null } = {}) => {
    if (!file) return;

    const targetProjectId = projectId || currentProject?.id || currentAnalysis?.project_id;
    if (!targetProjectId) {
      toast.error('Dr. Wold needs a project', {
        description: 'Select or create a project before running analysis.',
      });
      return;
    }

    if (aiGate.required && aiGate.checked && !aiGate.ready) {
      toast.error('Dr. Wold needs an engine key', {
        description: 'AI analysis is required, but no usable Google or OpenRouter key is configured. Settings is opening now.',
      });
      navigate('/settings');
      return;
    }

    setAnalyzing(true);
    setJobProgress(0);
    setPredictions(null);
    setTimeframeMethods([]);
    setSelectedCandidate(null);
    setSelectedCandidateIdx(0);
    setLastRescoreResult(null);

    const tempUrl = URL.createObjectURL(file);
    setPreviewUrl(tempUrl);

    try {
      const spanPayload = normalizeAnalysisSpan(overrideSpan || analysisSpan);
      const { job_id } = await API.uploadImage(targetProjectId, file, {
        userPivots,
        uploadSource: fromDoodle ? 'doodle_overlay' : 'file_upload',
        analysisFocus,
        analysisSpan: spanPayload,
      });
      const job = await API.pollJob(job_id, (j) => {
        if (typeof j.progress === 'number') setJobProgress(j.progress);
      }, 250);

      if (job.result_id) {
        const fullAnalysis = await API.getAnalysis(job.result_id);
        setCurrentAnalysis(fullAnalysis);
        setCurrentProject((prev) => upsertProjectAnalysis(prev, fullAnalysis));
        const preferred = fullAnalysis?.candidates?.find(c => c.preferred) || fullAnalysis?.candidates?.[0] || null;
        setSelectedCandidate(preferred);
        setSelectedCandidateIdx(preferred ? fullAnalysis.candidates.indexOf(preferred) : 0);
      }
    } catch (err) {
      console.error('Upload error:', err);
      API.showFriendlyApiError(err, 'analyze that chart', { id: 'analysis-upload' });
      if (!fromDoodle) URL.revokeObjectURL(tempUrl);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await analyzeImageFile(file);
    if (e.target) e.target.value = '';
  };

  const handleSubmitDoodle = async (blob, userPivots = []) => {
    if (!blob) return;

    const file = new File([blob], `doodle_${Date.now()}.png`, { type: 'image/png' });
    setDoodleSubmitting(true);
    try {
      await analyzeImageFile(file, { fromDoodle: true, userPivots });
    } finally {
      setDoodleSubmitting(false);
    }
  };

  const handleReanalyzeScope = async () => {
    if (!currentAnalysis?.id) return;

    try {
      if (lockScopeMode) {
        if (!selectedCandidate?.id) {
          throw new Error('Select a candidate before using Lock Scope reanalysis');
        }

        setRescoreBusy(true);
        const rescoreResult = await API.rescoreAnalysis(currentAnalysis.id, {
          analysis_id: currentAnalysis.id,
          candidate_id: selectedCandidate.id,
          existing_pivots: selectedCandidate.pivots || [],
          corrections: [],
          pattern_type: selectedCandidate.pattern_type,
          lock_scope_mode: true,
          bypass_vision: true,
          rederive_from_pivots_only: true,
        });

        setLastRescoreResult(rescoreResult);
        const updated = await API.getAnalysis(currentAnalysis.id);
        syncSelectionFromAnalysis(updated, selectedCandidate.id);
        return;
      }

      const originalUrl = API.getAnalysisChartUrl(currentAnalysis.id, {
        view: 'original',
        candidateId: selectedCandidate?.id,
        candidateIndex: selectedCandidateIdx,
        showFibs,
        showTimeframe,
        predictionSteps,
      });

      const fallbackUrl = getChartUrl('annotated');
      let response = await fetch(originalUrl, { cache: 'no-store' });
      if (!response.ok) {
        response = await fetch(fallbackUrl, { cache: 'no-store' });
      }
      if (!response.ok) {
        throw new Error('Unable to fetch chart image (' + response.status + ')');
      }

      const blob = await response.blob();
      const file = new File([blob], 'scope_reanalysis_' + Date.now() + '.png', { type: blob.type || 'image/png' });
      await analyzeImageFile(file, {
        fromDoodle: false,
        userPivots: [],
        overrideSpan: analysisSpan,
      });
    } catch (error) {
      console.error('Scope reanalysis failed:', error);
      API.showFriendlyApiError(error, 'reanalyze that locked scope', { id: 'analysis-scope' });
    } finally {
      if (lockScopeMode) {
        setRescoreBusy(false);
      }
    }
  };

  const handleApplyStrictnessPreview = async () => {
    if (!currentAnalysis?.id || !selectedCandidateId || strictnessApplyBusy) return;

    setStrictnessApplyBusy(true);
    try {
      const result = await API.applyStrictnessPreview(currentAnalysis.id, {
        candidateId: selectedCandidateId,
        candidateIndex: selectedCandidateIdx,
        strictness: strictnessProbe,
        maxTotal: 3,
      });

      setStrictnessPreview(result);
      setLastRescoreResult(null);
      const updated = await API.getAnalysis(currentAnalysis.id);
      syncSelectionFromAnalysis(updated, result?.preferred_candidate_id || null);
      toast.success('Strictness spotlight updated', {
        description: 'Strictness ' + (strictnessProbe ?? 0).toFixed(2) + ' is now driving the live candidate stack.',
      });
    } catch (error) {
      console.error('Strictness apply failed:', error);
      API.showFriendlyApiError(error, 'bring that strictness spotlight forward', { id: 'analysis-strictness-apply' });
    } finally {
      setStrictnessApplyBusy(false);
    }
  };

  const handleRefreshStrictnessPreview = () => {
    setStrictnessPreviewNonce((value) => value + 1);
  };

  const handleRestoreStrictnessStack = async () => {
    if (!currentAnalysis?.id || strictnessApplyBusy || !strictnessRestoreAvailable) return;

    setStrictnessApplyBusy(true);
    try {
      const result = await API.restoreStrictnessStack(currentAnalysis.id);
      const updated = await API.getAnalysis(currentAnalysis.id);
      syncSelectionFromAnalysis(updated, result?.preferred_candidate_id || null);
      setLastRescoreResult(null);
      toast.success('Original count restored', {
        description: 'The pre-spotlight candidate stack is back on deck.',
      });
    } catch (error) {
      console.error('Strictness restore failed:', error);
      API.showFriendlyApiError(error, 'restore that original count', { id: 'analysis-strictness-restore' });
    } finally {
      setStrictnessApplyBusy(false);
    }
  };

  const handleReleaseManualReplot = async () => {
    if (!currentAnalysis?.id || !manualPresentationMode) return;

    try {
      await API.updateAnalysisPresentationMode(currentAnalysis.id, 'default');
      const updated = await API.getAnalysis(currentAnalysis.id);
      syncSelectionFromAnalysis(updated, selectedCandidate?.id || null);
      toast.success('Automatic replot restored', {
        description: 'Manual anchor presentation is released. Live chart automation is back on.',
      });
    } catch (error) {
      console.error('Release manual replot failed:', error);
      API.showFriendlyApiError(error, 'release that manual replot', { id: 'analysis-release-manual-replot' });
    }
  };

  const handlePrefer = async (candidateId) => {
    try {
      await API.preferCandidate(currentAnalysis.id, candidateId);
      const updated = await API.getAnalysis(currentAnalysis.id);
      syncSelectionFromAnalysis(updated, candidateId);
    } catch (e) {
      console.error('Prefer failed:', e);
      API.showFriendlyApiError(e, 'set that preferred candidate', { id: 'analysis-prefer' });
    }
  };

  const priceFromSourceY = (sourceY) => {
    const meta = chartMetadata?.chart_meta;
    if (!meta) return null;

    const chartHeight = Math.max(1, Number(meta.chart_h || 1));
    const marginY = Number(meta.margin_y || 0);
    const priceMin = Number(meta.price_min ?? 0);
    const priceMax = Number(meta.price_max ?? 0);
    const priceRange = Math.max(1e-8, Number(meta.price_range || (priceMax - priceMin) || 1));

    const ratio = Math.min(1, Math.max(0, (sourceY - marginY) / chartHeight));
    return priceMax - ratio * priceRange;
  };

  const yPercentFromSourceY = (sourceY) => {
    const meta = chartMetadata?.chart_meta;
    if (!meta) return null;

    const chartHeight = Math.max(1, Number(meta.chart_h || 1));
    const marginY = Number(meta.margin_y || 0);
    const ratio = Math.min(1, Math.max(0, (sourceY - marginY) / chartHeight));
    return ratio * 100;
  };

  const xPercentFromSourceX = (sourceX) => {
    const meta = chartMetadata?.chart_meta;
    if (!meta) return null;

    const chartWidth = Math.max(1, Number(meta.chart_w || 1));
    const marginX = Number(meta.margin_x || 0);
    const ratio = Math.min(1, Math.max(0, (sourceX - marginX) / chartWidth));
    return ratio * 100;
  };

  const syncSelectionFromAnalysis = (updatedAnalysis, preferredCandidateId = null) => {
    setCurrentAnalysis(updatedAnalysis);
    setCurrentProject((prev) => upsertProjectAnalysis(prev, updatedAnalysis));

    const preferred = updatedAnalysis?.candidates?.find(c => c.id === preferredCandidateId)
      || updatedAnalysis?.candidates?.find(c => c.preferred)
      || updatedAnalysis?.candidates?.find(c => c.id === selectedCandidate?.id)
      || updatedAnalysis?.candidates?.[0]
      || null;

    setSelectedCandidate(preferred);
    setSelectedCandidateIdx(preferred ? updatedAnalysis.candidates.indexOf(preferred) : 0);
    return preferred;
  };

  const handleSelectAnalysis = async (analysisId) => {
    if (!analysisId) return;

    const known = (currentProject?.analyses || []).find((entry) => entry.id === analysisId);
    const nextAnalysis = known || await API.getAnalysis(analysisId);

    setCurrentAnalysis(nextAnalysis);
    setCurrentProject((prev) => upsertProjectAnalysis(prev, nextAnalysis));
    setSelectedCandidate(null);
    setSelectedCandidateIdx(0);
    setPredictions(null);
    setTimeframeMethods([]);
    setLastRescoreResult(null);
    setChartMetadata(null);
    if (typeof window !== 'undefined' && projectId) {
      window.localStorage.setItem(activeAnalysisStorageKey, analysisId);
    }
  };

  const handlePivotDragEnd = async (draftMap) => {
    if (!currentAnalysis?.id || !selectedCandidate || rescoreBusy) return;

    const entries = Object.entries(draftMap || {});
    if (!entries.length) return;

    const corrections = entries
      .map(([indexKey, draft]) => {
        const index = Number(indexKey);
        if (!Number.isInteger(index)) return null;

        const currentPivot = selectedCandidate.pivots?.[index];
        if (!currentPivot) return null;

        const newPrice = priceFromSourceY(draft?.sourceY);
        const newXPosition = xPercentFromSourceX(draft?.sourceX);
        const newYPosition = yPercentFromSourceY(draft?.sourceY);

        if (newPrice == null || Number.isNaN(newPrice)) return null;

        const oldPrice = Number(currentPivot.price);
        const oldXPosition = Number(currentPivot.x_position);
        const priceChanged = !Number.isFinite(oldPrice) || Math.abs(newPrice - oldPrice) >= 1e-6;
        const xChanged = Number.isFinite(newXPosition) && (!Number.isFinite(oldXPosition) || Math.abs(newXPosition - oldXPosition) >= 1e-3);
        const oldYPosition = Number(currentPivot.y_position);
        const yChanged = Number.isFinite(newYPosition) && (!Number.isFinite(oldYPosition) || Math.abs(newYPosition - oldYPosition) >= 1e-3);
        if (!priceChanged && !xChanged && !yChanged) return null;

        const payload = {
          index,
          new_price: Number(newPrice.toFixed(6)),
          original_price: Number.isFinite(oldPrice) ? oldPrice : null,
          label: currentPivot.label,
          lock: !!currentPivot.locked,
        };

        if (Number.isFinite(newXPosition)) {
          payload.new_x_position = Number(newXPosition.toFixed(3));
        }

        if (Number.isFinite(newYPosition)) {
          payload.new_y_position = Number(newYPosition.toFixed(3));
        }

        return payload;
      })
      .filter(Boolean);

    if (!corrections.length) return;

    setRescoreBusy(true);
    try {
      const rescoreResult = await API.rescoreAnalysis(currentAnalysis.id, {
        analysis_id: currentAnalysis.id,
        candidate_id: selectedCandidate.id,
        existing_pivots: selectedCandidate.pivots,
        corrections,
      });

      setLastRescoreResult(rescoreResult);

      const updated = await API.getAnalysis(currentAnalysis.id);
      syncSelectionFromAnalysis(updated, selectedCandidate.id);
    } catch (error) {
      console.error('Pivot rescore failed:', error);
      API.showFriendlyApiError(error, 'apply those pivot edits', { id: 'analysis-pivot-rescore' });
      throw error;
    } finally {
      setRescoreBusy(false);
    }
  };

  const strictnessSample = (strictnessInterval?.samples || []).reduce((best, sample) => {
    if (!best) return sample;
    const left = Math.abs((sample?.strictness ?? 0) - strictnessProbe);
    const right = Math.abs((best?.strictness ?? 0) - strictnessProbe);
    return left < right ? sample : best;
  }, null);

  const handleFractalZoom = async (waveIndex) => {
    if (!fractalEnabled || !currentAnalysis?.id || !selectedCandidateId) return;

    const nextPath = [...fractalPath, waveIndex];
    setFractalLoading(true);
    try {
      const view = await API.zoomFractal(currentAnalysis.id, {
        path: nextPath,
        candidateId: selectedCandidateId,
        candidateIndex: selectedCandidateIdx,
      });
      setFractalView(view);
      setFractalPath(nextPath);
    } catch (error) {
      console.error('Fractal zoom failed:', error);
      API.showFriendlyApiError(error, 'zoom that fractal branch', { id: 'analysis-fractal-zoom' });
    } finally {
      setFractalLoading(false);
    }
  };

  const handleFractalBack = async () => {
    if (!fractalEnabled || !currentAnalysis?.id || !selectedCandidateId) return;
    if (!fractalTree) return;

    const nextPath = fractalPath.slice(0, -1);
    if (nextPath.length === 0) {
      setFractalView(buildFractalRootView(fractalTree));
      setFractalPath([]);
      return;
    }

    setFractalLoading(true);
    try {
      const view = await API.zoomFractal(currentAnalysis.id, {
        path: nextPath,
        candidateId: selectedCandidateId,
        candidateIndex: selectedCandidateIdx,
      });
      setFractalView(view);
      setFractalPath(nextPath);
    } catch (error) {
      console.error('Fractal back navigation failed:', error);
      API.showFriendlyApiError(error, 'step back through that fractal tree', { id: 'analysis-fractal-back' });
    } finally {
      setFractalLoading(false);
    }
  };

  const saveBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleReportExport = async (mode = 'html') => {
    if (!currentAnalysis?.id || reportBusy) return;

    setReportBusy(true);
    try {
      const blob = await API.getPremiumReport(currentAnalysis.id, {
        candidateId: selectedCandidate?.id,
        candidateIndex: selectedCandidateIdx,
        sections: selectedReportSections.join(','),
      });
      const stamp = currentAnalysis.id?.slice(0, 8) || 'analysis';

      if (mode === 'pdf') {
        const html = await blob.text();
        const popup = window.open('', '_blank', 'noopener,noreferrer');
        if (!popup) {
          toast.error('Dr. Wold needs popup access', {
            description: 'Allow popups for this site so the print-ready report can open in a separate tab.',
          });
          return;
        }
        popup.document.open();
        popup.document.write(html);
        popup.document.close();
        popup.focus();
        setTimeout(() => popup.print(), 350);
        return;
      }

      saveBlob(blob, `wavekit_report_${stamp}.html`);
    } catch (error) {
      console.error('Report export failed:', error);
      API.showFriendlyApiError(error, 'export that report', { id: 'analysis-report-export' });
    } finally {
      setReportBusy(false);
    }
  };

  const handleSendChat = async () => {
    if (!chatInput.trim() || chatLoading || !currentAnalysis?.id) return;

    const userMsg = { role: 'user', content: chatInput };
    const nextMessages = [...chatMessages, userMsg];
    setChatMessages(nextMessages);
    setChatInput('');
    setChatLoading(true);

    try {
      const response = await API.sendAnalysisChat(currentAnalysis.id, {
        messages: nextMessages,
        candidateId: selectedCandidate?.id,
      });
      const assistantContent = response?.content || response?.response || '';
      setChatMessages(prev => [...prev, { role: 'assistant', content: assistantContent || 'No response.' }]);
    } catch (e) {
      console.error('Chat failed:', e);
      API.showFriendlyApiError(e, 'send that chat prompt', { id: 'analysis-chat' });
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Dr. Wold hit a snag while answering. Check the backend or try again in a moment.' }]);
    } finally {
      setChatLoading(false);
    }
  };

  const getChartUrl = (type) => {
    if (!currentAnalysis?.id) return '';
    return API.getAnalysisChartUrl(currentAnalysis.id, {
      view: type,
      candidateId: selectedCandidate?.id,
      candidateIndex: selectedCandidateIdx,
      showFibs,
      showTimeframe,
      predictionSteps,
      presentationMode: manualPresentationMode ? 'manual_only' : 'default',
    });
  };

  // Scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  return (
    <div className="p-4 md:p-6 space-y-6 relative">
      {/* Consensus Overlay - shown when multi-LLM analysis is running */}
      {consensusRunning && consensusAnalysisId && (
        <ConsensusOverlay
          analysisId={consensusAnalysisId}
          onComplete={(consensus) => {
            setConsensusRunning(false);
            setConsensusAnalysisId(null);
            if (consensus) {
              toast.success('Consensus Analysis Complete', {
                description: `Pattern: ${consensus.pattern_type} | Confidence: ${((consensus.confidence || 0) * 100).toFixed(0)}%`,
              });
              // Refresh the analysis to get updated data
              API.getAnalysis(consensusAnalysisId).then(setCurrentAnalysis).catch(console.error);
            }
          }}
          onGateFailed={(data) => {
            setConsensusRunning(false);
            setConsensusAnalysisId(null);
            toast.warning('Pattern Detection Failed', {
              description: data.message || 'No credible Elliott Wave pattern detected.',
            });
          }}
          onLayerComplete={(layer, pivots, confidence) => {
            // Could update UI progressively here if needed
            console.log(`Layer ${layer} complete: ${(confidence * 100).toFixed(0)}% confidence`);
          }}
        />
      )}
      
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight">
            {currentProject?.name || 'Project Analysis'}
          </h1>
          <p className="text-[#a1a1aa] text-sm">
            {currentAnalysis ? 'Detailed Wave Count & Predictions' : 'Upload a chart to begin Elliott Wave Analysis'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input type="file" ref={fileRef} className="hidden" accept="image/*" onChange={handleUpload} />
          
          {/* Run Consensus Button - appears when analysis exists */}
          {currentAnalysis?.id && !consensusRunning && (
            <button
              onClick={async () => {
                try {
                  setConsensusRunning(true);
                  setConsensusAnalysisId(currentAnalysis.id);
                  await API.runConsensusAnalysis(currentAnalysis.id);
                } catch (err) {
                  console.error('Failed to start consensus:', err);
                  setConsensusRunning(false);
                  setConsensusAnalysisId(null);
                  toast.error('Failed to start consensus analysis');
                }
              }}
              className="wk-btn-secondary flex items-center gap-2"
              data-testid="run-consensus-btn"
            >
              Multi-LLM Consensus
            </button>
          )}
          
          <button
            onClick={() => fileRef.current?.click()}
            disabled={analyzing || consensusRunning || (aiGate.required && aiGate.checked && !aiGate.ready)}
            className="wk-btn-primary flex items-center gap-2"
            data-testid="analyze-btn"
          >
            Analyze New Chart
          </button>
        </div>
      </div>

      <AnalysisView
        project={currentProject}
        analysis={currentAnalysis}
        analyzing={analyzing}
        jobProgress={jobProgress}
        chartView={chartView}
        setChartView={setChartView}
        showFibs={showFibs}
        setShowFibs={setShowFibs}
        showTimeframe={showTimeframe}
        setShowTimeframe={setShowTimeframe}
        predictionSteps={predictionSteps}
        completionPredictionSteps={completionPredictionSteps}
        predictions={predictions}
        predictionsLoading={predictionsLoading}
        expandedPredictions={expandedPredictions}
        setExpandedPredictions={setExpandedPredictions}
        expandedExplain={expandedExplain}
        setExpandedExplain={setExpandedExplain}
        expandedFibs={expandedFibs}
        setExpandedFibs={setExpandedFibs}
        selectedCandidate={selectedCandidate}
        setSelectedCandidate={setSelectedCandidate}
        selectedCandidateIdx={selectedCandidateIdx}
        setSelectedCandidateIdx={setSelectedCandidateIdx}
        analysisHistory={currentProject?.analyses || []}
        onSelectAnalysis={handleSelectAnalysis}
        timeframeMethods={timeframeMethods}
        selectedMethod={selectedMethod}
        setSelectedMethod={setSelectedMethod}
        chatMessages={chatMessages}
        chatInput={chatInput}
        setChatInput={setChatInput}
        chatLoading={chatLoading}
        handleUpload={handleUpload}
        handlePrefer={handlePrefer}
        handleSendChat={handleSendChat}
        getChartUrl={getChartUrl}
        previewUrl={previewUrl}
        fileRef={fileRef}
        chatEndRef={chatEndRef}
        navigate={navigate}
        aiGate={aiGate}
        chartMetadata={chartMetadata}
        onPivotDragEnd={handlePivotDragEnd}
        mapSourceYToPrice={priceFromSourceY}
        analysisFocus={analysisFocus}
        setAnalysisFocus={setAnalysisFocus}
        analysisSpan={analysisSpan}
        setAnalysisSpan={setAnalysisSpan}
        handleReanalyzeScope={handleReanalyzeScope}
        lockScopeMode={lockScopeMode}
        setLockScopeMode={setLockScopeMode}
        rescoreBusy={rescoreBusy}
        strictnessInterval={strictnessInterval}
        strictnessProbe={strictnessProbe}
        setStrictnessProbe={setStrictnessProbe}
        strictnessSample={strictnessSample}
        strictnessPreview={strictnessPreview}
        strictnessPreviewLoading={strictnessPreviewLoading}
        strictnessApplyBusy={strictnessApplyBusy}
        strictnessRestoreAvailable={strictnessRestoreAvailable}
        manualPresentationMode={manualPresentationMode}
        handleApplyStrictnessPreview={handleApplyStrictnessPreview}
        handleRefreshStrictnessPreview={handleRefreshStrictnessPreview}
        handleRestoreStrictnessStack={handleRestoreStrictnessStack}
        handleReleaseManualReplot={handleReleaseManualReplot}
        fractalEnabled={fractalEnabled}
        setFractalEnabled={setFractalEnabled}
        fractalView={fractalView}
        fractalPath={fractalPath}
        fractalLoading={fractalLoading}
        handleFractalZoom={handleFractalZoom}
        handleFractalBack={handleFractalBack}
        reportBusy={reportBusy}
        handleReportExport={handleReportExport}
        selectedReportSections={selectedReportSections}
        setSelectedReportSections={setSelectedReportSections}
        handleSubmitDoodle={handleSubmitDoodle}
        doodleSubmitting={doodleSubmitting}
        lastRescoreResult={lastRescoreResult}
        isCurator={isCurator}
        onSetAsLanding={handleSetAsLanding}
        setAsLandingBusy={landingBusy}
      />
    </div>
  );
}





































